/* ═══════════════════════════════════════════════════════════════
   Address sync — pure logic (MULTI_WINDOW_PLAN W2).
   A page that opens a record in its own state reports the record's
   address; Back from the record returns to the list. No React here.
   ═══════════════════════════════════════════════════════════════ */

export type SyncAction = { action: 'none' } | { action: 'push' | 'replace'; to: string };

function pathOf(address: string): string {
  const q = address.indexOf('?');
  return q === -1 ? address : address.slice(0, q);
}

/** The id segment when `loc` is `<listPath>/<id>`; null otherwise. */
export function recordIdAt(loc: string, listPath: string): string | null {
  const p = pathOf(loc);
  if (!p.startsWith(`${listPath}/`)) return null;
  const rest = p.slice(listPath.length + 1);
  if (!rest || rest.includes('/')) return null;
  try {
    return decodeURIComponent(rest);
  } catch {
    return null;
  }
}

/** The page's open record changed (`prev` -> `next`). Decide what to write.
 *  next set  : push the record's address (replace when only params differ);
 *              nothing when the location already IS that address (inbound
 *              open — no loop, no double entry).
 *  next null : the page closed its record: put the list address back, but
 *              only if the page had one open and the location still shows
 *              a record of this list. */
export function planAddressChange(
  prev: string | null,
  next: string | null,
  loc: string,
  listPath: string
): SyncAction {
  if (next !== null) {
    if (next === loc) return { action: 'none' };
    // First open that the location already shows (typed address with params).
    if (prev === null && pathOf(next) === pathOf(loc)) return { action: 'none' };
    return { action: pathOf(next) === pathOf(loc) ? 'replace' : 'push', to: next };
  }
  if (prev !== null && recordIdAt(loc, listPath) !== null) return { action: 'push', to: listPath };
  return { action: 'none' };
}

/** The location changed. True when it moved back to the bare list while the
 *  page still has a record open (Back) -> the page should close the record. */
export function shouldLeaveRecord(address: string | null, loc: string, listPath: string): boolean {
  return address !== null && pathOf(loc) === listPath;
}

/* Addresses a page just wrote: the shell must not re-consume them as a
   deep link (it would re-open — and reset — the record the page owns). */
const reported = new Set<string>();

export function reportAddress(address: string): void {
  reported.add(address);
}

/** One-shot: true if `address` was written by a page. */
export function takeReported(address: string): boolean {
  return reported.delete(address);
}

/* Record name for the tab title ("<record> · <parent> · SpecForma"). */
let recordTitle: string | null = null;
const titleListeners = new Set<() => void>();

export function setRecordTitle(name: string | null): void {
  if (recordTitle === name) return;
  recordTitle = name;
  titleListeners.forEach((l) => l());
}

export function subscribeRecordTitle(l: () => void): () => void {
  titleListeners.add(l);
  return () => titleListeners.delete(l);
}

export function getRecordTitle(): string | null {
  return recordTitle;
}
